import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Combobox, Option } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { DomainOption } from "../../types";

import { DomainComboboxTrigger } from "./DomainComboboxTrigger";

export type DomainComboboxProps = {
    options: DomainOption[];
    selectedValue: string | null;
    onSelect: (domain: string) => void;
    isInvalid?: boolean;
    errorMessage?: string;
    dataAutomation?: string;
};

export const DomainCombobox = ({
    options,
    selectedValue,
    onSelect,
    isInvalid,
    errorMessage,
    dataAutomation,
}: DomainComboboxProps) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [triggerWidth, setTriggerWidth] = useState<string>();

    // The combobox popover only matches the trigger width automatically for the
    // native Select trigger. With a custom trigger we measure the container and
    // feed the width back so the popover lines up with the form field.
    useLayoutEffect(() => {
        const element = containerRef.current;

        if (!element || typeof ResizeObserver === "undefined") {
            return;
        }

        const observer = new ResizeObserver(() => {
            setTriggerWidth(`${element.offsetWidth}px`);
        });

        observer.observe(element);

        return () => observer.disconnect();
    }, []);
    const [searchValue, setSearchValue] = useState("");

    const { primaryOptions, secondaryOptions } = useMemo(() => {
        const query = searchValue.trim().toLowerCase();
        const primary: DomainOption[] = [];
        const secondary: DomainOption[] = [];

        options.forEach((option) => {
            if (query.length && !option.label.toLowerCase().includes(query)) {
                return;
            }

            if (option.kind === "secondary") {
                secondary.push(option);
            } else {
                primary.push(option);
            }
        });

        return { primaryOptions: primary, secondaryOptions: secondary };
    }, [options, searchValue]);

    const selectedOption = useMemo(
        () => options.find((option) => option.id === selectedValue) ?? null,
        [options, selectedValue],
    );

    const handleSelect = (domain: string) => {
        onSelect(domain);
        setIsOpen(false);
    };

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);

        if (!open) {
            setSearchValue("");
        }
    };

    const renderOption = (option: DomainOption, badge?: boolean) => (
        <Combobox.Option
            key={option.id}
            value={option}
            textValue={option.id}
            isSelected={selectedValue === option.id}
            onClick={() => handleSelect(option.id)}
        >
            <Flex
                alignItems="center"
                gap="8px"
                width="100%"
            >
                {option.favicon ? <Option.Favicon src={option.favicon} /> : null}
                <Combobox.OptionLabel>{option.label}</Combobox.OptionLabel>
                {badge ? (
                    <Badge data-automation={`${dataAutomation}-secondary-badge`}>
                        {translate("activation.alerts.competitors.dropdown.secondary.badge")}
                    </Badge>
                ) : null}
            </Flex>
        </Combobox.Option>
    );

    return (
        <Stack
            ref={containerRef}
            gap="4px"
            flex="1"
            minWidth="0"
        >
            <Combobox
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                width={triggerWidth}
            >
                <Combobox.Trigger>
                    <DomainComboboxTrigger
                        data-automation={dataAutomation}
                        label={selectedOption?.label}
                        favicon={selectedOption?.favicon}
                        placeholder={translate("common.domain")}
                        isInvalid={isInvalid}
                    />
                </Combobox.Trigger>

                <Combobox.SearchInput
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder={translate("activation.alerts.competitors.dropdown.search.placeholder")}
                />

                <Combobox.Content>
                    {primaryOptions.length ? (
                        <Combobox.Section key="primary">
                            {primaryOptions.map((option) => renderOption(option))}
                        </Combobox.Section>
                    ) : null}

                    {secondaryOptions.length ? (
                        <Combobox.Section key="secondary">
                            <Combobox.SectionHeader>
                                {translate("activation.alerts.competitors.dropdown.secondary.section-header")}
                            </Combobox.SectionHeader>
                            {secondaryOptions.map((option) => renderOption(option, true))}
                        </Combobox.Section>
                    ) : null}
                </Combobox.Content>
            </Combobox>

            {errorMessage ? (
                <Text
                    fontVariant="caption"
                    color={colorsPalettes.red["400_P"]}
                >
                    {errorMessage}
                </Text>
            ) : null}
        </Stack>
    );
};
